import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { adminRunFeedAudit, type FeedAuditResult } from "@/lib/admin-feed-audit.functions";
import {
  getFeedAutoUpdateStatus,
  setFeedAutoUpdateEnabled,
  getFeedAutoUpdateLog,
} from "@/lib/admin-feed-auto-update.functions";
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/_authenticated/admin/feed-audit")({
  head: () => ({
    meta: [
      { title: "Admin — Feed & productaudit" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: FeedAuditPage,
});

function FeedAuditPage() {
  const run = useServerFn(adminRunFeedAudit);
  const getStatus = useServerFn(getFeedAutoUpdateStatus);
  const setEnabled = useServerFn(setFeedAutoUpdateEnabled);
  const getLog = useServerFn(getFeedAutoUpdateLog);
  const qc = useQueryClient();
  const [result, setResult] = useState<FeedAuditResult | null>(null);

  const statusQ = useQuery({
    queryKey: ["feed-auto-update-status"],
    queryFn: () => getStatus({}),
  });
  const logQ = useQuery({
    queryKey: ["feed-auto-update-log"],
    queryFn: () => getLog({}),
    refetchInterval: 30_000,
  });

  const toggleMut = useMutation({
    mutationFn: (enabled: boolean) => setEnabled({ data: { enabled } }),
    onSuccess: (s) => {
      qc.setQueryData(["feed-auto-update-status"], s);
      qc.invalidateQueries({ queryKey: ["feed-auto-update-log"] });
      toast.success(s.enabled ? "Automatische productupdates aan" : "Automatische productupdates uit");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Wijzigen mislukt"),
  });

  const mut = useMutation({
    mutationFn: () => run({}),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Audit klaar: ${r.ok}/${r.total} producten OK`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Audit mislukt"),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Feed & productaudit</h1>
          <p className="text-muted-foreground">
            Controleert of productdata compleet en consistent is voor Google Merchant
            Center / Google Ads (identifier, categorie, beschrijving, verzendkosten,
            voorraad-vs-actief).
          </p>
        </div>
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${mut.isPending ? "animate-spin" : ""}`} />
          {mut.isPending ? "Bezig…" : "Audit starten"}
        </button>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Zap className={`mt-1 h-5 w-5 ${statusQ.data?.enabled ? "text-emerald-600" : "text-muted-foreground"}`} />
            <div>
              <h2 className="font-semibold">Automatische productupdates</h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                Wanneer aan, worden prijs-, voorraad- en beschikbaarheidswijzigingen
                direct doorgevoerd in de Merchant Center-feed. Elke wijziging wordt
                hieronder gelogd.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                    statusQ.data?.enabled
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${statusQ.data?.enabled ? "bg-emerald-500" : "bg-muted-foreground"}`} />
                  {statusQ.isLoading ? "Laden…" : statusQ.data?.enabled ? "Actief" : "Uit"}
                </span>
                {statusQ.data?.updatedAt && (
                  <span className="text-muted-foreground">
                    Laatst gewijzigd: {new Date(statusQ.data.updatedAt).toLocaleString("nl-NL")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Switch
            checked={statusQ.data?.enabled ?? false}
            disabled={statusQ.isLoading || toggleMut.isPending}
            onCheckedChange={(v) => toggleMut.mutate(v)}
            aria-label="Automatische productupdates in- of uitschakelen"
          />
        </div>
      </div>

      <div className="rounded-lg border">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="font-semibold">Logboek automatische updates</h2>
          <span className="text-xs text-muted-foreground">
            {logQ.data?.length ?? 0} regels · auto-refresh 30s
          </span>
        </div>
        {logQ.isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Laden…</div>
        ) : (logQ.data?.length ?? 0) === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Nog geen automatische updates geregistreerd.
          </div>
        ) : (
          <ul className="divide-y">
            {logQ.data!.map((row) => (
              <li key={row.id} className="flex flex-wrap items-start justify-between gap-3 p-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">
                      {new Date(row.createdAt).toLocaleString("nl-NL")}
                    </span>
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        row.status === "ok"
                          ? "bg-emerald-100 text-emerald-700"
                          : row.status === "error"
                            ? "bg-red-100 text-red-700"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {row.status}
                    </span>
                    <span className="font-medium">{row.event}</span>
                  </div>
                  {row.productSlug && (
                    <div className="text-xs text-muted-foreground">/product/{row.productSlug}</div>
                  )}
                  {row.message && <div className="mt-1 text-muted-foreground">{row.message}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>



      {!result ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          Klik op "Audit starten" om alle producten te scannen.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Stat label="Totaal producten" value={result.total} />
            <Stat
              label="Zonder problemen"
              value={result.ok}
              accent="text-emerald-600"
              icon={<CheckCircle2 className="h-5 w-5" />}
            />
            <Stat
              label="Met problemen"
              value={result.issues.length}
              accent={result.issues.length > 0 ? "text-red-600" : "text-muted-foreground"}
              icon={<AlertTriangle className="h-5 w-5" />}
            />
          </div>

          {Object.keys(result.summary).length > 0 && (
            <div className="rounded-lg border p-4">
              <h2 className="mb-3 font-semibold">Meest voorkomende problemen</h2>
              <ul className="space-y-1 text-sm">
                {Object.entries(result.summary)
                  .sort(([, a], [, b]) => b - a)
                  .map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-4 border-b py-1 last:border-0">
                      <span>{k}</span>
                      <span className="font-mono text-muted-foreground">{v}×</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {result.issues.length > 0 && (
            <div className="rounded-lg border">
              <h2 className="border-b p-4 font-semibold">Producten met issues</h2>
              <ul className="divide-y">
                {result.issues.map((i) => (
                  <li key={i.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">{i.name || i.slug}</div>
                        <div className="text-xs text-muted-foreground">/product/{i.slug}</div>
                      </div>
                      {i.slug !== "(geen slug)" && (
                        <Link
                          to="/product/$slug"
                          params={{ slug: i.slug }}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                          target="_blank"
                          rel="noopener"
                        >
                          Bekijk <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                    <ul className="mt-2 list-disc pl-5 text-sm text-red-700">
                      {i.problems.map((p, idx) => (
                        <li key={idx}>{p}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Uitgevoerd: {new Date(result.ranAt).toLocaleString("nl-NL")}
          </p>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  icon,
}: {
  label: string;
  value: number;
  accent?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`mt-1 flex items-center gap-2 text-3xl font-bold ${accent ?? ""}`}>
        {icon}
        {value}
      </div>
    </div>
  );
}
